module Client.LocationEquipment

open Client
open Client.Services
open Shared
open global.Elmish
open System
open Thoth.Fetch
open Thoth.Json
open Fetch.Types
open Feliz
open Feliz.MaterialUI
open Fable.MaterialUI.Icons
open Fable.MaterialUI.MaterialDesignIcons
open Feliz.UseElmish
open Fable.Core.Experimental
open System.Text.RegularExpressions

type EquipmentView =
  | DefaultView
  | RequestSupplies
  | RequestService
  | EnterMeterReadings

let renderBreadcrumbs customerId equipmentNumberOption =
  let equipmentNumberLabel = match equipmentNumberOption with | None -> "Equipment" | Some name -> $"Equipment: %s{name}"
  Mui.breadcrumbs [
    breadcrumbs.children [
      Mui.link [
        prop.href (Client.Pages.toHash Pages.Page.Home)
        link.children "Home"
      ]
      Mui.link [
        prop.href (Client.Pages.toHash Pages.Page.Locations)
        link.children "Locations"
      ]
      Mui.link [
        prop.href (Client.Pages.toHash (Pages.Page.Location customerId))
        link.children "Location"
      ]
      Mui.typography [
        typography.color.textPrimary
        typography.children equipmentNumberLabel
      ]
    ]
  ]
let view = React.functionComponent (fun (inputProps: {| customerId : int; equipmentId : int; model : LoggedInModel; |}) ->
  let classes = Styles.useStyles ()
  let (equipment, setEquipment) = React.useState<Shared.Equipment option>(None)
  let (error, setError) = React.useState<string option>(None)
  let (isLoading, setIsLoading) = React.useState(true)
  let (viewMode, setViewMode) = React.useState(EquipmentView.DefaultView)

  React.useEffectOnce(fun () ->
    promise {
      let! authHeader = Services.getAuthHeader()
      try
        let decoder = Services.decoder<Result<Shared.Equipment,string>>
        let! response = Fetch.tryGet($"/api/Location/%i{inputProps.customerId}/Equipment/{inputProps.equipmentId}", headers = [ authHeader ], decoder = decoder)
        match response with
        | Ok v ->
          match v with
          | Ok equipmentData ->
            setEquipment (Some equipmentData)
          | Error msg ->
            setError (Some msg)
        | Result.Error fetchError ->
          match fetchError with
          | FetchError.DecodingFailed _ -> setError (Some "Problem parsing response")
          | FetchError.FetchFailed msg -> setError (Some "Problem fetching equipment")
          | FetchError.NetworkError _ -> setError (Some "Problem fetching equipment")
          | FetchError.PreparingRequestFailed _ -> setError (Some "Problem fetching equipment")
      with e ->
        setError (Some e.Message)
      setIsLoading false
    } |> ignore
  )

  Html.div [
    renderBreadcrumbs inputProps.customerId (equipment |> Option.map (fun e -> e.EquipmentNumber))
    if isLoading then
      Html.h2 "Equipment"
      Mui.circularProgress []
    else
      match error with
      | Some msg -> Html.p [ prop.style [ style.color.red ]; prop.text msg ]
      | None ->
        match equipment with
        | None ->
          Html.h2 "Equipment not found"
        | Some eq ->
          Html.div [
            Html.h2 (sprintf "Equipment: %s" eq.EquipmentNumber)
            Mui.typography [
              typography.variant.body2
              typography.color.textSecondary
              prop.text (sprintf "Serial: %s" eq.SerialNumber)
            ]
            Mui.typography [
              typography.variant.body2
              // typography.component' "span"
              typography.color.textSecondary
              prop.text (sprintf "Location: %s" eq.Location)
            ]
            let viewSupplies = viewMode = EquipmentView.RequestSupplies
            let viewService = viewMode = EquipmentView.RequestService
            let viewMeterReadings = viewMode = EquipmentView.EnterMeterReadings
            Mui.accordion [
              accordion.defaultExpanded false
              accordion.expanded viewSupplies
              accordion.onChange (fun _ _ -> if viewSupplies then setViewMode EquipmentView.DefaultView else setViewMode EquipmentView.RequestSupplies)
              accordion.children [
                Mui.accordionSummary [
                  accordionSummary.expandIcon (expandMoreIcon [ ])
                  accordionSummary.children [
                    Mui.grid [
                      grid.container true
                      grid.spacing._2
                      grid.alignItems.center
                      grid.children [
                        Mui.grid [
                          grid.item true
                          grid.children [
                            Mui.typography [
                              typography.variant.h6
                              typography.children "Supplies"
                            ]
                          ]
                        ]
                        Mui.grid [
                          grid.item true
                          grid.children [
                            waterIcon [ ]
                          ]
                        ]
                      ]
                    ]
                  ]
                ]
                Mui.accordionDetails [
                  Supplies.view {| equipmentOption = Some eq; reCaptchaSiteKey = inputProps.model.reCaptchaSiteKey; |}
                ]
              ]
            ]
            Mui.accordion [
              accordion.defaultExpanded false
              accordion.expanded viewService
              accordion.onChange (fun _ _ -> if viewService then setViewMode EquipmentView.DefaultView else setViewMode EquipmentView.RequestService)
              accordion.children [
                Mui.accordionSummary [
                  accordionSummary.expandIcon (expandMoreIcon [ ])
                  accordionSummary.children [
                    Mui.grid [
                      grid.container true
                      grid.spacing._2
                      grid.alignItems.center
                      grid.children [
                        Mui.grid [
                          grid.item true
                          grid.children [
                            Mui.typography [
                              typography.variant.h6
                              typography.children "Service"
                            ]
                          ]
                        ]
                        Mui.grid [
                          grid.item true
                          grid.children [
                            toolsIcon [ ]
                          ]
                        ]
                      ]
                    ]
                  ]
                ]
                Mui.accordionDetails [
                  ServiceCalls.view {| equipmentOption = Some eq; reCaptchaSiteKey = inputProps.model.reCaptchaSiteKey; |}
                ]
              ]
            ]
            Mui.accordion [
              accordion.defaultExpanded false
              accordion.expanded viewMeterReadings
              accordion.onChange (fun _ _ -> if viewMeterReadings then setViewMode EquipmentView.DefaultView else setViewMode EquipmentView.EnterMeterReadings)
              accordion.children [
                Mui.accordionSummary [
                  accordionSummary.expandIcon (expandMoreIcon [ ])
                  accordionSummary.children [
                    Mui.grid [
                      grid.container true
                      grid.spacing._2
                      grid.alignItems.center
                      grid.children [
                        Mui.grid [
                          grid.item true
                          grid.children [
                            Mui.typography [
                              typography.variant.h6
                              typography.children "Meter Readings"
                            ]
                          ]
                        ]
                        Mui.grid [
                          grid.item true
                          grid.children [
                            fileDocumentIcon [ ]
                          ]
                        ]
                      ]
                    ]
                  ]
                ]
                Mui.accordionDetails [
                  MeterReadings.view {| equipmentOption = Some eq; reCaptchaSiteKey = inputProps.model.reCaptchaSiteKey; |}
                ]
              ]
            ]
          ]
  ]
)
